import { z } from 'zod';

const hostname = z.string().trim().toLowerCase().max(253)
  .regex(/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/);
export const mailCertificateRenewalSchema = z.object({
  enabled: z.boolean(),
  certificates: z.array(z.object({
    hostname,
    source: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('plesk') }).strict(),
      z.object({ kind: z.literal('traefik'), serverUuid: z.string().regex(/^[a-zA-Z0-9-]{1,100}$/) }).strict(),
    ]),
    /** SHA-256 of the SSH host key, hex as returned by ssh2 hostHash. */
    sshHostKeySha256: z.string().regex(/^[a-f0-9]{64}$/),
    /** Refresh only an already staged webmail route on this pinned server. */
    webmailBridge: z.object({
      serverUuid: z.string().regex(/^[a-zA-Z0-9-]{1,100}$/),
      sshHostKeySha256: z.string().regex(/^[a-f0-9]{64}$/),
    }).strict().optional(),
  }).strict()).max(50),
}).strict().superRefine((value, ctx) => {
  if (value.enabled && value.certificates.length === 0)
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['certificates'], message: 'Add at least one certificate before enabling renewal' });
  value.certificates.forEach((item, index) => {
    if (item.webmailBridge && !item.hostname.startsWith('mail.'))
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['certificates', index, 'hostname'], message: 'Webmail refresh requires a mail.domain hostname' });
  });
  if (new Set(value.certificates.map(c => c.hostname)).size !== value.certificates.length)
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['certificates'], message: 'Certificate hostnames must be unique' });
});
export type MailCertificateRenewalConfig = z.infer<typeof mailCertificateRenewalSchema>;
export interface MailCertificateRenewalResult {
  hostname: string;
  state: 'healthy' | 'renewed' | 'failed';
  expiresAt: string | null;
  /** Allowlisted diagnostics only; never upstream response bodies. */
  error: 'source_unavailable' | 'invalid_certificate' | 'install_failed' | 'probe_failed' | 'webmail_refresh_failed' | 'expiring' | null;
}
export interface MailCertificateRenewalDto {
  config: MailCertificateRenewalConfig;
  running: boolean;
  lastRunAt: string | null;
  results: MailCertificateRenewalResult[];
}
